import { useState } from "react";
import { useMutation } from "convex/react";
import { CircleDashed, PencilLine } from "lucide-react";
import { statutScorecard, type StatutScorecard } from "@repo/ent-struct";
import { Badge, Button, Dialog } from "@repo/ui";
import { api } from "../../../convex/_generated/api";
import type { Id } from "../../../convex/_generated/dataModel";
import { messageErreur } from "./edition";

const LIBELLES_STATUT: Record<StatutScorecard, string> = {
  brouillon: "Brouillon",
  publiee: "Publiée",
  modifiee: "Modifications non publiées",
};

/** Pastille de statut (listes). */
export function BadgeStatut({
  numeroCourant,
  numeroPublie,
}: {
  numeroCourant: number;
  numeroPublie: number | null;
}) {
  const statut = statutScorecard(numeroCourant, numeroPublie);
  return (
    <Badge
      variant={statut === "publiee" ? "success" : statut === "brouillon" ? "secondary" : "warning"}
      className="whitespace-nowrap"
    >
      {LIBELLES_STATUT[statut]}
    </Badge>
  );
}

/** Sous-titre de l'éditeur : statut et versions, en texte seul (rendu dans un <p>). */
export function resumeStatut(
  numeroCourant: number,
  numeroPublie: number | null,
): string {
  switch (statutScorecard(numeroCourant, numeroPublie)) {
    case "brouillon":
      return `Brouillon · version ${numeroCourant}, jamais publiée`;
    case "publiee":
      return `Publiée · version ${numeroCourant}`;
    case "modifiee":
      return `Version ${numeroCourant} · la version ${numeroPublie} est en service`;
  }
}

/**
 * Bandeau qui dit ce que voient les campagnes : rien (brouillon), ou une
 * version plus ancienne que celle à l'écran (modifications non publiées).
 */
export function BandeauStatut({
  numeroCourant,
  numeroPublie,
  modele,
}: {
  numeroCourant: number;
  numeroPublie: number | null;
  /** Modèle global : on l'assigne aux organisations, pas aux campagnes. */
  modele: boolean;
}) {
  const statut = statutScorecard(numeroCourant, numeroPublie);
  if (statut === "publiee") return null;
  return (
    <p className="flex items-start gap-2 rounded-surface border border-border bg-muted p-3 text-sm">
      {statut === "brouillon" ? (
        <>
          <CircleDashed className="mt-0.5 h-4 w-4 shrink-0" />
          {modele
            ? "Brouillon : ce modèle n'est assignable à aucune organisation. Publiez-le une fois complet."
            : "Brouillon : cette scorecard n'est proposée à aucune campagne. Publiez-la une fois complète."}
        </>
      ) : (
        <>
          <PencilLine className="mt-0.5 h-4 w-4 shrink-0" />
          Modifications non publiées : {modele ? "les nouvelles assignations reçoivent" : "les nouvelles campagnes utilisent"}{" "}
          encore la version {numeroPublie}. Publiez pour leur donner la version{" "}
          {numeroCourant}.
        </>
      )}
    </p>
  );
}

/**
 * Confirmation avant publication. La version publiée est celle que le serveur
 * a en version courante, et que l'utilisateur a sous les yeux : un
 * enregistrement venu d'ailleurs entre-temps fait échouer la publication.
 */
export function DialoguePublier({
  scorecardId,
  numero,
  onFermer,
  onPubliee,
}: {
  scorecardId: Id<"scorecards">;
  numero: number;
  onFermer: () => void;
  onPubliee: (numero: number) => void;
}) {
  const publier = useMutation(api.scorecards.publierScorecard);
  const [enCours, setEnCours] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);

  const confirmer = async () => {
    setEnCours(true);
    setErreur(null);
    try {
      const resultat = await publier({ scorecardId, numeroVersionAttendu: numero });
      onPubliee(resultat.numero);
    } catch (e) {
      setErreur(messageErreur(e));
      setEnCours(false);
    }
  };

  return (
    <Dialog isOpen onClose={onFermer} title={`Publier la version ${numero} ?`} className="max-w-lg">
      <p className="mb-4 text-sm text-muted-foreground">
        Les nouvelles campagnes et les copies utiliseront cette version. Les
        campagnes déjà lancées gardent la leur.
      </p>
      {erreur && (
        <p role="alert" className="mb-4 text-sm text-destructive">
          {erreur}
        </p>
      )}
      <div className="flex justify-end gap-2">
        <Button variant="ghost" onClick={onFermer}>
          Annuler
        </Button>
        <Button disabled={enCours} onClick={() => void confirmer()}>
          Publier
        </Button>
      </div>
    </Dialog>
  );
}
